<script setup>
import { ref } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  error: {
    type: String,
    default: null
  },
  selectedRegistrationType: {
    type: String,
    default: ''
  },
  registrationTypes: {
    type: Array,
    required: true
  },
  showRegistrationTypeDropdown: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits([
  'select-registration-type', 
  'toggle-registration-dropdown',
  'trigger-file-select', 
  'trigger-ai-flow',
  'file-dropped'
]);

// Drag and drop state
const isDragging = ref(false);
const dragCounter = ref(0);

// Handle drag events
const handleDragEnter = (e) => {
  e.preventDefault();
  dragCounter.value++;
  isDragging.value = true;
};

const handleDragLeave = (e) => {
  e.preventDefault();
  dragCounter.value--;
  if (dragCounter.value === 0) {
    isDragging.value = false;
  }
};

const handleDragOver = (e) => {
  e.preventDefault();
};

const handleDrop = (e) => {
  e.preventDefault();
  e.stopPropagation();
  isDragging.value = false;
  dragCounter.value = 0;
  
  const files = e.dataTransfer.files;
  if (files.length > 0) {
    const file = files[0];
    
    // Validate file type
    const fileExtension = file.name.toLowerCase().split('.').pop();
    if (['csv', 'xlsx'].includes(fileExtension)) {
      emit('file-dropped', file);
    } else {
      // File type not supported - parent will handle the error display
      emit('file-dropped', null, 'Formato de arquivo não suportado. Use apenas arquivos .csv ou .xlsx');
    }
  }
};
</script>

<template>
  <div class="p-6">
    <!-- Error message -->
    <div v-if="error" class="bg-n-ruby-1 dark:bg-n-ruby-3 p-4 rounded-lg border border-n-ruby-4 dark:border-n-ruby-6 mb-4">
      <div class="flex items-start">
        <i class="i-lucide-alert-triangle text-n-ruby-11 mt-1 mr-2"></i>
        <p class="text-sm text-n-ruby-11">{{ error }}</p>
      </div>
    </div>
    
    <div 
      class="text-center py-10 border-2 border-dashed rounded-lg transition-all duration-200"
      :class="isDragging 
        ? 'border-n-brand bg-n-brand/5 dark:bg-n-brand/10' 
        : 'border-n-slate-3 dark:border-n-slate-7 bg-n-slate-1 dark:bg-n-slate-1'"
      @drop="handleDrop"
      @dragover="handleDragOver"
      @dragenter="handleDragEnter"
      @dragleave="handleDragLeave"
    >
      <div class="flex flex-col items-center max-w-md mx-auto">
        <!-- Icon -->
        <i 
          class="text-5xl mb-3 transition-colors duration-200"
          :class="isDragging 
            ? 'i-lucide-file-plus text-n-brand' 
            : 'i-lucide-file-spreadsheet text-n-slate-9 dark:text-n-slate-10'"
        ></i>
        
        <!-- Drag and drop text -->
        <div v-if="isDragging" class="text-sm font-medium text-n-brand mb-2">
          Solte o arquivo aqui
        </div>
        
        <!-- Format info -->
        <div class="text-xs text-n-slate-9 dark:text-n-slate-11 text-center mb-6">
          <p>Arraste e solte um arquivo aqui ou clique em Importar</p>
          <p class="mt-1">Suporta os formatos de planilha Excel (.xlsx) e CSV (.csv)</p>
        </div>
        
        <!-- Dropdown Button -->
        <div class="relative w-full mb-4 filter-actions">
          <label class="block text-sm font-medium text-n-slate-12 mb-2 text-left">
            Baixar planilha modelo
          </label>
          <div 
            class="rounded-lg button-group outline-1 outline outline-n-container w-full">
            <Button
              :label="selectedRegistrationType ? registrationTypes.find(t => t.value === selectedRegistrationType)?.label : 'Selecione o tipo'"
              size="sm"
              color="slate"
              class="rounded-r-none !outline-0 bg-n-background dark:bg-n-slate-3 flex-1 min-w-0 !justify-start !h-12"
              @click="emit('toggle-registration-dropdown', $event)"
            />
            <Button
              icon="i-lucide-chevron-down"
              size="sm"
              color="slate"
              class="rounded-l-none !outline-0 bg-n-background dark:bg-n-slate-3 !h-12"
              trailing-icon
              @click="emit('toggle-registration-dropdown', $event)"
            />
          </div>
        </div>

        <!-- Import File Button -->
        <div class="relative w-full mb-4">
          <button
            class="w-full flex items-center justify-center gap-2 px-4 py-3 bg-n-background dark:bg-n-slate-3 border border-n-brand text-n-brand rounded-lg text-sm font-medium transition-all duration-200 hover:bg-n-brand/10 dark:hover:bg-n-brand/20 focus:bg-n-brand/10 dark:focus:bg-n-brand/20 focus:outline-none"
            @click="emit('trigger-file-select')"
          >
            <i class="i-lucide-download flex-shrink-0"></i>
            <span>Importar</span>
          </button>
        </div>
        
        <!-- AI Mapping Button -->
        <div class="relative w-full">
          <button
            class="w-full flex items-center justify-center gap-2 px-4 py-3 bg-n-brand text-white rounded-lg text-sm font-medium transition-all duration-200 hover:bg-n-brand/90 focus:bg-n-brand/90 focus:outline-none"
            @click="emit('trigger-ai-flow')"
          >
            <i class="i-lucide-sparkles flex-shrink-0"></i>
            <span>Mapear com IA</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.filter-actions {
  .button-group {
    &:hover {
      button {
        @apply bg-n-brand/10 dark:bg-n-brand/20;
      }
    }
    
    button {
      @apply transition-colors duration-200;
      
      &:hover {
        @apply bg-n-brand/10 dark:bg-n-brand/20;
      }
    }
  }
}
</style>